// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button/Button";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@insite/mobius/index";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import constants from "@store/constants";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import sortBy from "lodash/sortBy";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    ctaLabel = "ctaLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.ctaLabel]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    const pagesDataView = getDataView(state.data.pages, {
        parentNodeId: currentPage.nodeId,
    });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pagesDataView.value) {
        links = pagesDataView.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }

    return {
        pages: {
            ...pagesDataView,
            value: pagesDataView.value?.filter(x => x.fields["excludeFromNavigation"] === false),
        },
        links,
        currentPage,
        pagesDataView,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface MyReportsListStyles {
    wrapper: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    tableHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    tableCellName?: DataTableCellProps;
    tableCell?: DataTableCellProps;
}

export const reportsListStyles: MyReportsListStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(50), pxToRem(50))}
        `,
    },
    tableCellName: {
        typographyProps: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.bold};
            `,
        },
    },
};

const styles = reportsListStyles;

const MyReportsList: FC<Props> = ({ currentPage, links, fields: { ctaLabel }, pages, loadPagesForParent }) => {
    const history = useHistory();

    useEffect(() => {
        if (!pages.value) {
            loadPagesForParent({
                parentNodeId: currentPage.nodeId,
            });
        }
    }, [pages]);

    const goToUrl = (url: string) => {
        history.push(url);
    };

    if (!pages) {
        return null;
    }

    const sortedPages = sortBy(pages.value, o => o.fields["title"] || "");

    const reportCount = pages.value?.length;
    const headingText = translate("Available Reports");

    return (
        <StyledWrapper {...styles.wrapper}>
            <CardListHeading heading={headingText} hasNoResults={reportCount === 0} />
            <LoadingOverlay loading={pages?.isLoading || !pages?.value} {...styles.loadingOverlay}>
                <DataTable {...styles.table}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.tableHeader}>{translate("Report Name")}</DataTableHeader>
                        <DataTableHeader {...styles.tableHeader}>{translate("Link")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {sortedPages.map((page: PageProps) => (
                            <DataTableRow key={page.id} {...styles.tableRow}>
                                <DataTableCell {...styles.tableCellName}>{page.fields["title"]}</DataTableCell>
                                <DataTableCell {...styles.tableCell}>
                                    <Button
                                        onClick={() => goToUrl(`${links[page.nodeId]?.url}`)}
                                        variant="tertiary"
                                        sizeVariant="small"
                                    >
                                        {ctaLabel}
                                    </Button>
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(MyReportsList)),
    definition: {
        displayName: "Children Pages List",
        group: constants.widgetGroup.myReports,
        icon: "grid",
        fieldDefinitions: [
            {
                name: fields.ctaLabel,
                displayName: "CTA Label",
                editorTemplate: "TextField",
                defaultValue: "View Report",
                fieldType: "Translatable",
            },
        ],
        allowedContexts: [constants.pageType.myReports],
    },
};

export default widgetModule;
